// @ts-strict-ignore
import React, { useMemo } from 'react';
import { Checkbox } from '@usealloy/component-library';

import { snakeToCaps } from '~/helpers/formatting';
import {
  checkAllChildren,
  countChildren,
  countSelected,
  filterEmptyObjectsFromEntries,
  formatArrayText,
  getCollapseChecked,
  shouldBeCollapsible,
} from './helpers';
import { CollapseChildrenWrapper, StyledCollapse } from './styles';
import MetadataCheckbox from './MetadataCheckbox';

interface MetadataCollapseProps {
  header: string;
  values: any;
  setTabCount: (Function) => void;
  setWhitelist: (object) => void;
  whitelist: any;
}

const MetadataCollapse = ({
  header,
  values,
  setTabCount,
  setWhitelist,
  whitelist,
}: MetadataCollapseProps) => {
  const numberOfChildren = useMemo(
    () => countChildren(values),
    [values],
  );
  const numberOfSelectedChildren = useMemo(
    () => countSelected(whitelist, values),
    [whitelist, values],
  );
  const lastCheckedKey = Object.keys(whitelist).filter(key => typeof whitelist[key] !== 'object' && whitelist[key]).pop();
  if (typeof values === 'object' && whitelist === true) {
    checkAllChildren(values, setWhitelist, true);
  }
  const valuesToRender = Object.entries(values).filter(filterEmptyObjectsFromEntries);
  const collapseCheckedValue = getCollapseChecked(values, whitelist);
  return (
    <StyledCollapse
      collapsed={false}
      header={(
        <Checkbox
          data-testid={`collapse-checkbox-${header}`}
          checked={collapseCheckedValue === 'checked'}
          indeterminate={collapseCheckedValue === 'indeterminate'}
          label={header}
          onChange={event => {
            checkAllChildren(values, setWhitelist, event.target.checked);
            setTabCount(value => (
              event.target.checked
                ? value + (numberOfChildren - numberOfSelectedChildren)
                : value - numberOfSelectedChildren
            ));
          }}
          style={{ fontWeight: 700, whiteSpace: 'nowrap' }}
        />
      )}
    >
      {valuesToRender.map(([key, value]) => (
        <CollapseChildrenWrapper key={`metadata-collapse-${header}-child-${key}`}>
          {
            shouldBeCollapsible(value)
              ? (
                <MetadataCollapse
                  header={snakeToCaps(key)}
                  values={value}
                  setTabCount={setTabCount}
                  setWhitelist={
                    collapseValues => { setWhitelist({ ...whitelist, [key]: collapseValues }); }
                  }
                  whitelist={whitelist?.[key] || {}}
                />
              ) : (
                <MetadataCheckbox
                  checked={whitelist?.[key]}
                  handleHideParentKeys={() => {
                    setWhitelist({
                      ...whitelist,
                      [key]: whitelist?.[key] === 'top' ? true : 'top',
                    });
                  }}
                  handleToggle={checked => {
                    if (!checked) {
                      delete whitelist[key];
                      setWhitelist({ ...whitelist });
                      setTabCount(count => count - 1);
                    } else {
                      setWhitelist({ ...whitelist, [key]: checked });
                      setTabCount(count => count + 1);
                    }
                  }}
                  hideParentKeys={whitelist?.[key] === 'top'}
                  label={key}
                  showToggle={key === lastCheckedKey}
                  value={Array.isArray(value) ? formatArrayText(value) : value}
                />
              )
          }
        </CollapseChildrenWrapper>
      ))}
    </StyledCollapse>
  );
};

export default MetadataCollapse;
